import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, ArrowLeft, Users, Briefcase, DollarSign, Star, TrendingUp, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users?limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/payments/all`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTransactions(transactionsRes.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[#94A3B8] mb-1">{title}</p>
          <p className="text-3xl font-bold text-[#0F172A] font-['Space_Grotesk']">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#003B5C] font-semibold">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad Admin</span>
            </button>

            <div className="flex items-center space-x-4">
              <span className="text-[#475569]">Admin: <span className="font-semibold text-[#003B5C]">{user?.name}</span></span>
              <Button
                onClick={logout}
                variant="outline"
                className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white rounded-full"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">Admin Dashboard</h1>
          <p className="text-[#475569]">Platform overview and management</p>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard title="Total Users" value={stats.total_users} icon={Users} color="bg-[#003B5C]" />
            <StatCard title="Active Workers" value={stats.total_workers} icon={Briefcase} color="bg-[#FFB81C]" />
            <StatCard title="Total Jobs" value={stats.total_jobs} icon={FileText} color="bg-[#009B4D]" />
            <StatCard 
              title="Total Revenue" 
              value={`JMD $${stats.total_revenue.toFixed(2)}`} 
              icon={DollarSign} 
              color="bg-[#003B5C]" 
            />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-4 border-b-2 font-medium transition-colors duration-200 ${
                  activeTab === 'stats'
                    ? 'border-[#003B5C] text-[#003B5C]'
                    : 'border-transparent text-[#94A3B8] hover:text-[#003B5C]'
                }`}
              >
                Statistics
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 border-b-2 font-medium transition-colors duration-200 ${
                  activeTab === 'users'
                    ? 'border-[#003B5C] text-[#003B5C]'
                    : 'border-transparent text-[#94A3B8] hover:text-[#003B5C]'
                }`}
              >
                Recent Users
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className={`py-4 border-b-2 font-medium transition-colors duration-200 ${
                  activeTab === 'transactions'
                    ? 'border-[#003B5C] text-[#003B5C]'
                    : 'border-transparent text-[#94A3B8] hover:text-[#003B5C]'
                }`}
              >
                Recent Transactions
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'stats' && stats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-[#F9F7F2] rounded-lg">
                  <p className="text-[#94A3B8] mb-2">Customers</p>
                  <p className="text-3xl font-bold text-[#003B5C]">{stats.total_customers}</p>
                </div>
                <div className="text-center p-6 bg-[#F9F7F2] rounded-lg">
                  <p className="text-[#94A3B8] mb-2">Total Reviews</p>
                  <p className="text-3xl font-bold text-[#003B5C]">{stats.total_reviews}</p>
                </div>
                <div className="text-center p-6 bg-[#F9F7F2] rounded-lg">
                  <p className="text-[#94A3B8] mb-2">Transactions</p>
                  <p className="text-3xl font-bold text-[#003B5C]">{stats.total_transactions}</p>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td className="px-4 py-3 text-sm text-[#0F172A]">{u.name}</td>
                        <td className="px-4 py-3 text-sm text-[#475569]">{u.email}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            u.role === 'worker' ? 'bg-[#FFB81C]/10 text-[#FFB81C]' : 'bg-[#003B5C]/10 text-[#003B5C]'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#94A3B8]">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0F172A]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {transactions.map((t) => (
                      <tr key={t.id}>
                        <td className="px-4 py-3 text-sm text-[#475569]">
                          {new Date(t.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#0F172A] capitalize">
                          {t.payment_type.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3 text-sm font-bold text-[#003B5C]">
                          {t.currency} ${t.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            t.payment_status === 'paid' ? 'bg-[#009B4D]/10 text-[#009B4D]' : 'bg-[#FFB81C]/10 text-[#FFB81C]'
                          }`}>
                            {t.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;